import { useDispatch } from 'react-redux';
import { cloneDeep } from 'es-toolkit';

import { logError } from '@zg-rentals/log-error';
import { useQuery } from '@zg-rentals/react-query5';

import PropertiesActions from '../../actions/PropertiesActions';
import { logInfo } from '../../loggers/appLogger';
import makeRequest from '../../utils/makeRequestUtils';

import Constants from '../../constants';
import { RENTAL_MANAGER_API_V2_PREFIX } from '../../constants/RouteConstants';
import TimeConstants from '../../constants/TimeConstants';
import ContactEmailDeliverability from '../../model/ContactEmailDeliverability';
import Freemium from '../../model/Freemium';
import PropertyOverview from '../../model/PropertyOverview';
import RentGuarantee from '../../model/RentGuarantee';

import useRequestConfig from '../useRequestConfig';

/**
 * TODO: refactor to pass queryParams, queryOptions as arguments
 * queryParams will handle accepting multiple parameters
 * and save the second argument for queryOptions
 *
 * when using this query we need to sync data to the redux store.
 * call syncOnDataChangePropertyDetailsOverview on data change
 *
 *   useEffect(() => {
 *     if (propertyDetailsOverviewData) {
 *       syncOnDataChangePropertyDetailsOverview({ data: propertyDetailsOverviewData, currentProperty, dispatch });
 *     }
 *   }, [propertyDetailsOverviewData]);
 */
const useGetPropertyDetailsOverview = (listingAlias, includeRestrictions = true) => {
  const dispatch = useDispatch();
  const requestConfig = useRequestConfig();

  const queryFn = () =>
    makeRequest({
      endpoint: `${RENTAL_MANAGER_API_V2_PREFIX}/users/properties/details/overview`,
      config: { params: { propertyId: listingAlias, includeRestrictions } },
      dispatch,
      requestConfig,
    });

  const query = useQuery({
    queryKey: ['getPropertyDetailsOverview', listingAlias, includeRestrictions],
    queryFn: async () => {
      const data = await queryFn();

      return data;
    },
    enabled: Boolean(listingAlias),
    staleTime: TimeConstants.SECONDS_10,
    // TODO: refactor to not use onError callback
    onError: (err) => {
      // eslint-disable-next-line curly-quotes/no-straight-quotes
      if (err.message === "You don't have permission to see that listing") {
        logInfo({
          message: err.message,
          context: 'useGetPropertyDetailsOverview',
        });
      } else {
        logError({
          error: err,
          context: {
            listingAlias,
            details: 'useGetPropertyDetailsOverview.error',
          },
        });
      }
      return err;
    },
  });

  return query;
};

const setQueryFreemiumActiveProduct = ({ config, payload }) => {
  const { queryClient, listingAlias } = config;
  const { activeProduct } = payload;

  queryClient.setQueryData(['getPropertyDetailsOverview', listingAlias], (oldData) => {
    if (!oldData)
      return {
        freemium: {
          activeProduct: {
            ...activeProduct,
          },
        },
      };

    const newData = cloneDeep(oldData);
    newData.freemium.activeProduct = { ...activeProduct };

    return newData;
  });
};

const syncOnDataChangePropertyDetailsOverview = ({ data, currentProperty, dispatch }) => {
  syncPropertyOverview({ data, dispatch });
  syncCurrentProperty({ data, currentProperty, dispatch });
};

const syncCurrentProperty = ({ data, currentProperty, dispatch }) => {
  const currentPropertyMap = {
    dateLastActivatedTimestamp: String(data?.lastActivatedMs || ''),
    dateUpdatedTimestamp: String(data?.updatedMs || ''),
  };

  dispatch(
    PropertiesActions.updateCurrentProperty({
      contactEmailDeliverability: ContactEmailDeliverability(data.contactEmailDeliverability),
      active: data.status === 'LISTED FOR RENT',
      freemium: Freemium(data.freemium),
      street: data.address.street,
      city: data.address.city,
      state: data.address.state,
      listingTypeCode: data.listingType.code,
      zip: data.address.zip,
      unit: data.address.unit,
      isMultifamily: data.isMultifamily,
      isArchived: data.isArchived,
      restrictions: {
        ...currentProperty.restrictions,
        ...data?.restrictions,
      },
      listingAlias: data.listingAlias,
      listingAvailability: data.listingAvailability || {},
      rentalProtection: RentGuarantee(data.rentGuarantee),
      updateActiveStatus: data.updateActiveStatus,
      ...currentPropertyMap,
    }),
  );
};

const syncPropertyOverview = ({ data, dispatch }) => {
  const propertyOverview = PropertyOverview(data);

  dispatch({
    type: Constants.UPDATE_PROPERTY_OVERVIEW,
    payload: propertyOverview,
  });
};

export { setQueryFreemiumActiveProduct, syncOnDataChangePropertyDetailsOverview, useGetPropertyDetailsOverview };
